/**
 * BoroughAmplificationMap — public social-proof grid on the Public Dashboard.
 *
 * Reads /api/share-events/by-district and renders one tile per district +
 * the borough-level tile, with a heat-coloured bar showing relative
 * confirmed-post volume vs the leader. Empty state is intentionally
 * provocative ("Be the first — your borough is watching.") to convert
 * visitors into amplifiers. Each tile is clickable: scrolls back to the
 * hero share buttons (for borough) or links into /search?district=… so
 * the visitor can immediately grab and share that ward's card.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, ArrowUpRight, Sparkles, Activity, Trophy, Flame } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// District display names — keep in sync with /api/postcodes
const DISTRICT_NAMES = {
  TS15: 'Yarm / Kirklevington',
  TS16: 'Eaglescliffe',
  TS17: 'Thornaby / Ingleby Barwick',
  TS18: 'Stockton Town Centre',
  TS19: 'Hartburn / Roseworth',
  TS20: 'Norton',
  TS21: 'Sedgefield Rural',
  TS22: 'Billingham West',
  TS23: 'Billingham North',
};

// All known share targets — borough first, then districts in order.
// We render every target so the grid never looks empty even before
// a single confirmed post arrives.
const ALL_TARGETS = [
  'borough',
  'TS15', 'TS16', 'TS17', 'TS18',
  'TS19', 'TS20', 'TS21', 'TS22', 'TS23',
];

function targetLabel(t) {
  return t === 'borough' ? 'Stockton-on-Tees' : t;
}
function targetSub(t) {
  return t === 'borough' ? 'Borough-wide' : (DISTRICT_NAMES[t] || 'Stockton ward');
}

// Heat scale from low (subdued) to leader (full orange) — designed to read
// at a glance without being garish. Returns an inline-style rgba colour.
function heatStyle(value, max) {
  if (max <= 0) return { bgColor: 'rgba(255,255,255,0.06)', barColor: 'rgba(255,255,255,0.10)', glow: 0 };
  const t = Math.min(1, value / max);
  const orange = [201, 79, 26];                 // brand-orange
  const dim = [60, 40, 24];                      // deep ember
  const mix = (a, b, k) => Math.round(a + (b - a) * k);
  const [r, g, b] = [
    mix(dim[0], orange[0], t),
    mix(dim[1], orange[1], t),
    mix(dim[2], orange[2], t),
  ];
  return {
    bgColor: `rgba(${r}, ${g}, ${b}, ${0.10 + 0.18 * t})`,
    barColor: `rgba(201, 79, 26, ${0.55 + 0.45 * t})`,
    glow: t,
  };
}

function relativeTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (sec < 60) return 'just now';
    if (sec < 3600) return `${Math.round(sec / 60)} min ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)} hr ago`;
    return `${Math.round(sec / 86400)} d ago`;
  } catch {
    return null;
  }
}

export default function BoroughAmplificationMap({ onShareCtaClick }) {
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [r30, r7] = await Promise.all([
          fetch(`${API}/api/share-events/by-district?days=30`),
          fetch(`${API}/api/share-events/by-district?days=7`),
        ]);
        if (!r30.ok || !r7.ok) throw new Error('http');
        const [j30, j7] = await Promise.all([r30.json(), r7.json()]);
        if (!cancelled) {
          setData(j30);
          setWeekly(j7);
        }
      } catch {
        if (!cancelled) {
          setData({ districts: [], max_confirmed: 0, total_confirmed: 0, total_actions: 0, days: 30, active_targets: 0 });
          setWeekly({ districts: [], max_confirmed: 0, total_confirmed: 0, total_actions: 0, days: 7, active_targets: 0 });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Poll every 60s — this section earns its keep when it visibly updates.
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) {
    return (
      <section className="py-16 sm:py-20 bg-[#15130F]" data-testid="borough-amplification-map-loading">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="h-6 w-40 bg-white/[0.06] rounded animate-pulse mb-3" />
          <div className="h-10 w-72 bg-white/[0.06] rounded animate-pulse mb-8" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-28 bg-white/[0.04] rounded-[6px] animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Build a complete district list — known targets + any unexpected ones from API.
  const byTarget = Object.fromEntries((data?.districts || []).map(d => [d.target, d]));
  const seen = new Set();
  const tiles = [];
  for (const t of ALL_TARGETS) {
    seen.add(t);
    tiles.push(byTarget[t] || { target: t, actions: 0, confirmed_posts: 0, last_activity: null });
  }
  // Trail any unknown targets (defensive)
  for (const d of (data?.districts || [])) {
    if (!seen.has(d.target)) tiles.push(d);
  }

  // Sort: confirmed posts desc, then actions desc, with borough always first
  tiles.sort((a, b) => {
    if (a.target === 'borough' && b.target !== 'borough') return -1;
    if (b.target === 'borough' && a.target !== 'borough') return 1;
    if (a.confirmed_posts !== b.confirmed_posts) return b.confirmed_posts - a.confirmed_posts;
    return b.actions - a.actions;
  });

  const max = data?.max_confirmed || 0;
  const totalConfirmed = data?.total_confirmed || 0;
  const activeTargets = data?.active_targets || 0;
  const isEmpty = totalConfirmed === 0;

  // Weekly leader (excludes the borough-level row — wards only compete with wards)
  const weeklyDistricts = (weekly?.districts || []).filter(d => d.target !== 'borough');
  const weeklyLeader = weeklyDistricts
    .filter(d => d.confirmed_posts > 0)
    .sort((a, b) => b.confirmed_posts - a.confirmed_posts || b.actions - a.actions)[0] || null;
  const weeklyRunnerUp = weeklyDistricts
    .filter(d => d.confirmed_posts > 0 && d.target !== weeklyLeader?.target)
    .sort((a, b) => b.confirmed_posts - a.confirmed_posts)[0] || null;
  const weeklyTotalConfirmed = weekly?.total_confirmed || 0;

  return (
    <section
      className="py-16 sm:py-20 bg-[#15130F] text-white relative overflow-hidden"
      data-testid="borough-amplification-map"
    >
      {/* subtle decoration */}
      <div className="absolute inset-0 pointer-events-none opacity-50"
           style={{ background: 'radial-gradient(circle at 80% 30%, rgba(201,79,26,0.12), transparent 55%), radial-gradient(circle at 10% 90%, rgba(45,110,78,0.08), transparent 50%)' }} />

      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 relative">
        {/* header */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2D6E4E] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2D6E4E]" />
              </span>
              <span className="font-display text-[11px] font-extrabold tracking-[0.18em] text-[#2D6E4E] uppercase">
                Borough Amplification · Live
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tighter leading-[1.05] max-w-[680px]">
              Wards on the move.<br />
              <span className="text-white/60">Confirmed LinkedIn posts, last 30 days.</span>
            </h2>
          </div>

          {/* Live counters */}
          <div className="flex items-center gap-6">
            <div>
              <div className="font-display text-3xl font-extrabold leading-none">{totalConfirmed.toLocaleString()}</div>
              <div className="font-body text-[10px] uppercase tracking-wider text-white/45 mt-1.5">Posts confirmed</div>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <div className="font-display text-3xl font-extrabold leading-none">{activeTargets}</div>
              <div className="font-body text-[10px] uppercase tracking-wider text-white/45 mt-1.5">Wards amplifying</div>
            </div>
          </div>
        </div>

        {/* ─── Top Amplifier of the Week strip ─── */}
        {weeklyLeader && (
          <div
            className="relative rounded-[8px] overflow-hidden mb-8 border border-[#FE4A2A]/35 bg-gradient-to-r from-[#2A1810] via-[#1F1812] to-[#090909]"
            data-testid="amplification-weekly-leader"
          >
            <div className="absolute inset-0 opacity-60 pointer-events-none"
                 style={{ background: 'radial-gradient(circle at 12% 50%, rgba(201,79,26,0.28), transparent 45%)' }} />
            <div className="relative flex items-center gap-4 sm:gap-6 px-4 sm:px-6 py-4">
              {/* Trophy icon */}
              <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#FE4A2A]/15 border border-[#FE4A2A]/40 flex items-center justify-center relative">
                <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-[#FFB48E]" strokeWidth={1.6} />
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FE4A2A] opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[#FE4A2A]" />
                </span>
              </div>

              {/* Leader text */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <Flame className="w-3 h-3 text-[#FE4A2A]" />
                  <span className="font-display text-[10px] sm:text-[11px] font-extrabold tracking-[0.18em] text-[#FFB48E] uppercase">
                    Top Amplifier · This Week
                  </span>
                </div>
                <p className="font-display text-base sm:text-lg lg:text-xl font-extrabold leading-tight">
                  <Link
                    to={`/search?district=${weeklyLeader.target}`}
                    className="text-white hover:text-[#FFB48E] transition-colors"
                    data-testid="amplification-weekly-leader-link"
                  >
                    {weeklyLeader.target}
                  </Link>
                  <span className="text-white/55"> ({DISTRICT_NAMES[weeklyLeader.target] || 'Stockton ward'})</span>{' '}
                  <span className="text-white/55">leads with</span>{' '}
                  <span className="text-[#FFB48E]">{weeklyLeader.confirmed_posts} post{weeklyLeader.confirmed_posts === 1 ? '' : 's'}</span>{' '}
                  <span className="text-white/55">this week.</span>
                </p>
                <p className="font-body text-xs sm:text-[13px] text-white/55 mt-1.5">
                  {weeklyRunnerUp ? (
                    weeklyLeader.confirmed_posts - weeklyRunnerUp.confirmed_posts === 0 ? (
                      <>
                        <span className="text-white/75">{weeklyRunnerUp.target}</span> is{' '}
                        <span className="text-white/75">tied</span>.
                        Pull ahead with the next post.
                      </>
                    ) : (
                      <>
                        <span className="text-white/75">{weeklyRunnerUp.target}</span> is{' '}
                        <span className="text-white/75">{weeklyLeader.confirmed_posts - weeklyRunnerUp.confirmed_posts} behind</span>.
                        Who&apos;ll catch them?
                      </>
                    )
                  ) : (
                    <>The only ward posting this week. Set the bar.</>
                  )}
                </p>
              </div>

              {/* CTA */}
              <button
                type="button"
                onClick={onShareCtaClick}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full bg-[#FE4A2A] hover:bg-[#F14728] transition-colors font-body text-[11px] sm:text-[12px] font-medium text-white whitespace-nowrap"
                data-testid="amplification-weekly-leader-cta"
              >
                <span className="hidden sm:inline">Take the lead</span>
                <span className="sm:hidden">Catch up</span>
                <ArrowUpRight className="w-3 h-3" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        )}

        {/* Empty-state nudge */}
        {isEmpty && (
          <div className="rounded-[6px] bg-gradient-to-br from-[#2A2620] to-[#090909] border border-[#FE4A2A]/30 p-5 mb-8" data-testid="amplification-empty-state">
            <div className="flex items-center gap-3 mb-1.5">
              <Sparkles className="w-4 h-4 text-[#FE4A2A]" />
              <span className="font-display text-[11px] font-extrabold tracking-wider text-[#FE4A2A] uppercase">No posts yet — be the first.</span>
            </div>
            <p className="font-body text-sm text-white/75 max-w-[640px]">
              Your borough is watching. The first ward to post their reality card sets the tone for everyone else.
              <button
                type="button"
                onClick={onShareCtaClick}
                className="ml-2 font-medium text-white underline-offset-2 hover:text-[#FE4A2A] hover:underline transition-colors"
                data-testid="amplification-empty-cta"
              >
                Grab a card and post it →
              </button>
            </p>
          </div>
        )}

        {/* Heat grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="amplification-grid">
          {tiles.map((t, idx) => {
            const heat = heatStyle(t.confirmed_posts, max);
            const pct = max > 0 ? Math.round((t.confirmed_posts / max) * 100) : 0;
            const TileWrap = t.target === 'borough'
              ? ({ children }) => (
                  <button
                    type="button"
                    onClick={onShareCtaClick}
                    className="w-full text-left"
                    data-testid={`amplification-tile-${t.target}`}
                  >{children}</button>
                )
              : ({ children }) => (
                  <Link
                    to={`/search?district=${t.target}`}
                    className="block"
                    data-testid={`amplification-tile-${t.target}`}
                  >{children}</Link>
                );

            return (
              <TileWrap key={t.target}>
                <div
                  className="group relative rounded-[6px] border border-white/10 hover:border-[#FE4A2A]/40 transition-all duration-300 p-3.5 hover:-translate-y-0.5"
                  style={{
                    background: heat.bgColor,
                    boxShadow: heat.glow > 0.4 ? `0 0 ${20 + heat.glow * 30}px rgba(201,79,26,${0.10 + heat.glow * 0.18})` : 'none',
                  }}
                >
                  {/* Top row: target + leader badge */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-display text-[15px] font-extrabold leading-none">{targetLabel(t.target)}</div>
                      <div className="font-body text-[10px] text-white/45 mt-1 leading-tight">{targetSub(t.target)}</div>
                    </div>
                    {idx === 0 && t.confirmed_posts > 0 && (
                      <span className="font-display text-[8.5px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-[3px] bg-[#FE4A2A]/25 text-[#FFB48E] border border-[#FE4A2A]/40 whitespace-nowrap">
                        Leading
                      </span>
                    )}
                  </div>

                  {/* Big number */}
                  <div className="flex items-baseline gap-1.5 mt-3">
                    <span
                      className="font-display font-extrabold text-3xl leading-none"
                      style={{ color: t.confirmed_posts > 0 ? '#FFFFFF' : 'rgba(255,255,255,0.30)' }}
                    >
                      {t.confirmed_posts}
                    </span>
                    <span className="font-body text-[10px] text-white/45 uppercase tracking-wider">posts</span>
                  </div>

                  {/* Heat bar */}
                  <div className="h-1 mt-3 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: heat.barColor }}
                    />
                  </div>

                  {/* Footer line */}
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-body text-[9.5px] text-white/40 inline-flex items-center gap-1">
                      <Activity className="w-2.5 h-2.5" strokeWidth={2.2} />
                      {t.actions} action{t.actions === 1 ? '' : 's'}
                    </span>
                    {t.last_activity ? (
                      <span className="font-body text-[9.5px] text-white/40">{relativeTime(t.last_activity)}</span>
                    ) : (
                      <span className="font-body text-[9.5px] text-white/30 italic">silent</span>
                    )}
                  </div>

                  {/* Hover CTA */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-6 h-6 rounded-full bg-[#FE4A2A] flex items-center justify-center">
                      <ArrowUpRight className="w-3 h-3 text-white" strokeWidth={2.5} />
                    </div>
                  </div>
                </div>
              </TileWrap>
            );
          })}
        </div>

        {/* Footer line */}
        <div className="mt-8 flex items-center justify-between flex-wrap gap-3 pt-5 border-t border-white/10">
          <p className="font-body text-[11px] text-white/35 inline-flex items-center gap-1.5">
            <Linkedin className="w-3 h-3 text-[#0A66C2]" />
            Self-reported via the <span className="text-white/60">Posted on LinkedIn?</span> follow-up.
            Updates every minute.
          </p>
          <button
            type="button"
            onClick={onShareCtaClick}
            className="font-body text-[11px] text-white/55 hover:text-[#FE4A2A] transition-colors inline-flex items-center gap-1 group"
            data-testid="amplification-footer-cta"
          >
            Add your post to the count
            <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}
